import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';

// Autocomplétion des gares/stations d'Île-de-France (Île-de-France Mobilités, gratuit, sans clé).
// onSelect reçoit { nom, modes: [], lignes: [], lat, lon }
const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

export const GareAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder,
  className,
  id,
  'data-testid': testId,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const skipNext = useRef(false);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    const q = (value || '').trim();
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const url = `https://data.iledefrance-mobilites.fr/api/records/1.0/search/?dataset=emplacement-des-gares-idf&q=${encodeURIComponent(q)}&rows=40`;
        const res = await fetch(url, { signal: ctrl.signal });
        const data = await res.json();
        const records = data.records || [];
        // Agrège par nom de gare : réunit modes + lignes, garde les coordonnées
        const byName = new Map();
        for (const rec of records) {
          const f = rec.fields || {};
          const nom = f.nom_gares || f.nom_long || f.nom;
          if (!nom) continue;
          const key = norm(nom);
          if (!byName.has(key)) {
            byName.set(key, { nom, modes: new Set(), lignes: new Set(), lat: null, lon: null });
          }
          const g = byName.get(key);
          if (f.mode) g.modes.add(String(f.mode).toUpperCase());
          if (f.indice_lig) g.lignes.add(String(f.indice_lig));
          if (g.lat == null && Array.isArray(f.geo_point_2d)) {
            g.lat = f.geo_point_2d[0];
            g.lon = f.geo_point_2d[1];
          }
        }
        const nq = norm(q);
        const list = Array.from(byName.values())
          .map((g) => ({ nom: g.nom, modes: Array.from(g.modes), lignes: Array.from(g.lignes), lat: g.lat, lon: g.lon }))
          .sort((a, b) => {
            const as = norm(a.nom).startsWith(nq) ? 0 : 1;
            const bs = norm(b.nom).startsWith(nq) ? 0 : 1;
            return as - bs || a.nom.localeCompare(b.nom);
          })
          .slice(0, 8);
        setSuggestions(list);
        setOpen(focusedRef.current && list.length > 0);
        setActive(-1);
      } catch (e) { /* réseau/abort : saisie libre conservée */ }
    }, 250);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const label = (g) => [g.nom, ...g.modes, ...g.lignes].join(' · ');

  const choose = (g) => {
    skipNext.current = true;
    onSelect(g);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div className="relative" ref={boxRef}>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
        autoComplete="off"
        onFocus={() => { focusedRef.current = true; }}
        onBlur={() => { focusedRef.current = false; }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); choose(suggestions[active]); }
          else if (e.key === 'Escape') { setOpen(false); }
        }}
      />
      {open && (
        <ul
          className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-y-auto"
          data-testid={testId ? `${testId}-suggestions` : undefined}
        >
          {suggestions.map((g, i) => (
            <li
              key={`${g.nom}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); choose(g); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 text-sm cursor-pointer ${i === active ? 'bg-[#FAF6EE] text-[#8F7344]' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              <span className="font-medium">{g.nom}</span>
              {(g.modes.length > 0 || g.lignes.length > 0) && (
                <span className="text-xs text-slate-400"> · {[...g.modes, ...g.lignes].join(' · ')}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GareAutocomplete;
